/**
 * Waseem Baby Product Store - Automated Dynamic Policy & Page Translator
 * Automatically translates Shopify Policies (Contact, Legal Notice, Privacy, Refund, Shipping, Terms)
 * and Page content across English (en), German (de), French (fr), and Dutch (nl).
 */

(function () {
  'use strict';

  const POLICY_TRANSLATIONS = {
    // 1. Contact Information
    contact: {
      en: {
        title: 'Company Details & Contact',
        intro: 'Do you have a question about your order, a product, or our service? Feel free to contact us. We are happy to help you.',
        companyDetails: 'Company Details',
        companyName: 'Company Name',
        tradeName: 'Trade Name',
        address: 'Business Address',
        kvk: 'Chamber of Commerce (KvK) Number',
        vat: 'VAT Number',
        email: 'Email',
        phone: 'Phone',
        responseNote: 'We aim to respond to all inquiries within 24 hours on business days.',
      },
      de: {
        title: 'Unternehmensdaten & Kontakt',
        intro: 'Haben Sie eine Frage zu Ihrer Bestellung, einem Produkt oder unserem Service? Zögern Sie nicht, uns zu kontaktieren. Wir helfen Ihnen gerne weiter.',
        companyDetails: 'Unternehmensdaten',
        companyName: 'Firmenname',
        tradeName: 'Handelsname',
        address: 'Geschäftsadresse',
        kvk: 'Handelsregisternummer (KvK)',
        vat: 'USt-IdNr.',
        email: 'E-Mail',
        phone: 'Telefon',
        responseNote: 'Wir bemühen uns, alle Anfragen an Werktagen innerhalb von 24 Stunden zu beantworten.',
      },
      fr: {
        title: 'Informations sur l’entreprise & contact',
        intro: 'Vous avez une question concernant votre commande, un produit ou notre service ? N’hésitez pas à nous contacter. Nous sommes à votre disposition pour vous aider.',
        companyDetails: 'Informations sur l’entreprise',
        companyName: 'Raison sociale',
        tradeName: 'Nom commercial',
        address: 'Adresse du siège',
        kvk: 'Numéro d’immatriculation (KvK)',
        vat: 'Numéro de TVA',
        email: 'E-mail',
        phone: 'Téléphone',
        responseNote: 'Nous nous efforçons de répondre à toutes les demandes sous 24 heures les jours ouvrés.',
      }
    },

    // 2. Shipping Policy
    shipping: {
      en: {
        title: 'Shipping Policy',
        heading1: 'Delivery Times & Shipping Costs',
        p1: 'We process and ship all orders with great care. Orders placed on business days before 22:00 are dispatched the same day.',
        nl_be: 'Netherlands & Belgium: 1 - 2 business days (Free shipping on orders over €50)',
        de: 'Germany: 2 - 4 business days (Free shipping on orders over €75)',
        lu_fr: 'Luxembourg & France: 2 - 4 business days (Free shipping on orders over €100)',
        track: 'Track & Trace: Once your order is dispatched, you will automatically receive a tracking link via email to follow your package.',
      },
      de: {
        title: 'Versandrichtlinie',
        heading1: 'Lieferzeiten & Versandkosten',
        p1: 'Wir bearbeiten und versenden alle Bestellungen mit größter Sorgfalt. Bestellungen, die an Werktagen vor 22:00 Uhr eingehen, werden noch am selben Tag versandt.',
        nl_be: 'Niederlande & Belgien: 1 - 2 Werktage (Kostenloser Versand ab 50 €)',
        de: 'Deutschland: 2 - 4 Werktage (Kostenloser Versand ab 75 €)',
        lu_fr: 'Luxemburg & Frankreich: 2 - 4 Werktage (Kostenloser Versand ab 100 €)',
        track: 'Sendungsverfolgung: Sobald Ihre Bestellung versandt wurde, erhalten Sie automatisch einen Tracking-Link per E-Mail.',
      },
      fr: {
        title: 'Politique d’expédition',
        heading1: 'Délais de livraison & Frais de port',
        p1: 'Nous traitons et expédions toutes les commandes avec le plus grand soin. Les commandes passées les jours ouvrés avant 22h00 sont expédiées le jour même.',
        nl_be: 'Pays-Bas & Belgique : 1 à 2 jours ouvrés (Livraison gratuite dès 50 €)',
        de: 'Allemagne : 2 à 4 jours ouvrés (Livraison gratuite dès 75 €)',
        lu_fr: 'Luxembourg & France : 2 à 4 jours ouvrés (Livraison gratuite dès 100 €)',
        track: 'Suivi de commande : Dès l’expédition de votre colis, vous recevrez automatiquement un lien de suivi par e-mail.',
      }
    },

    // 3. Refund & Return Policy
    refund: {
      en: {
        title: 'Refund & Return Policy',
        heading1: '14-Day Cooling-Off Period (Money-Back Guarantee)',
        p1: 'You have the right to return your order within 14 days of receipt without providing any reason. The product must be unused, in its original condition, and in the original packaging.',
        heading2: 'How to Return an Item',
        p2: '1. Contact our customer service team to register your return.<br>2. Pack the item securely with all original accessories.<br>3. Once received and inspected, your full purchase amount will be refunded within 5-7 business days.',
      },
      de: {
        title: 'Widerrufs- & Rückgaberichtlinie',
        heading1: '14 Tage Bedenkzeit (Geld-zurück-Garantie)',
        p1: 'Sie haben das Recht, Ihre Bestellung innerhalb von 14 Tagen nach Erhalt ohne Angabe von Gründen zurückzusenden. Der Artikel muss unbenutzt und in der Originalverpackung sein.',
        heading2: 'So funktioniert die Rücksendung',
        p2: '1. Kontaktieren Sie unseren Kundenservice, um Ihre Rücksendung anzumelden.<br>2. Verpacken Sie den Artikel sorgfältig mit allem Zubehör.<br>3. Nach Prüfung der Rücksendung wird der volle Betrag innerhalb von 5–7 Werktagen erstattet.',
      },
      fr: {
        title: 'Politique de remboursement & retour',
        heading1: 'Délai de rétractation de 14 jours (Garantie satisfait ou remboursé)',
        p1: 'Vous disposez d’un délai de 14 jours à compter de la réception de votre commande pour nous la retourner sans motif. Le produit doit être neuf et dans son emballage d’origine.',
        heading2: 'Comment effectuer un retour ?',
        p2: '1. Contactez notre service client pour enregistrer votre retour.<br>2. Emballez soigneusement l’article avec tous ses accessoires.<br>3. Après réception et vérification, le montant total vous sera remboursé sous 5 à 7 jours ouvrés.',
      }
    },

    // 4. Privacy Policy
    privacy: {
      en: {
        title: 'Privacy Policy',
        heading1: 'Protection of Your Personal Data',
        p1: 'Liefjes & Co B.V. respects your privacy and ensures that the personal information you provide is treated confidentially and in full accordance with the General Data Protection Regulation (GDPR).',
      },
      de: {
        title: 'Datenschutzerklärung',
        heading1: 'Schutz Ihrer persönlichen Daten',
        p1: 'Liefjes & Co B.V. respektiert Ihre Privatsphäre und stellt sicher, dass alle persönlichen Informationen vertraulich und gemäß der Datenschutz-Grundverordnung (DSGVO) behandelt werden.',
      },
      fr: {
        title: 'Politique de confidentialité',
        heading1: 'Protection de vos données personnelles',
        p1: 'Liefjes & Co B.V. respecte votre vie privée et veille à ce que vos données personnelles soient traitées de manière confidentielle et en totale conformité avec le RGPD.',
      }
    },

    // 5. Terms of Service
    terms: {
      en: {
        title: 'Terms of Service',
        heading1: 'General Terms & Conditions',
        p1: 'These general terms and conditions apply to all offers, orders, and agreements of Liefjes & Co B.V. By placing an order, you agree to these terms.',
      },
      de: {
        title: 'Allgemeine Geschäftsbedingungen (AGB)',
        heading1: 'Allgemeine Bedingungen',
        p1: 'Diese Allgemeinen Geschäftsbedingungen gelten für alle Angebote, Bestellungen und Verträge von Liefjes & Co B.V. Mit der Aufgabe einer Bestellung erklären Sie sich mit diesen Bedingungen einverstanden.',
      },
      fr: {
        title: 'Conditions générales de vente (CGV)',
        heading1: 'Conditions générales',
        p1: 'Les présentes conditions générales de vente s’appliquent à toutes les offres, commandes et contrats conclus avec Liefjes & Co B.V. Toute commande implique l’acceptation de ces conditions.',
      }
    },

    // 6. Legal Notice
    legal: {
      en: {
        title: 'Legal Notice / Impressum',
        heading1: 'Legal Information',
        p1: 'Liefjes & Co B.V. is a registered company in the Netherlands. All contents, designs, and images on this website are protected by copyright.',
      },
      de: {
        title: 'Impressum / Rechtliche Hinweise',
        heading1: 'Rechtliche Angaben',
        p1: 'Liefjes & Co B.V. ist ein in den Niederlanden eingetragenes Unternehmen. Alle Inhalte, Designs und Bilder auf dieser Website sind urheberrechtlich geschützt.',
      },
      fr: {
        title: 'Mentions légales',
        heading1: 'Informations juridiques',
        p1: 'Liefjes & Co B.V. est une société enregistrée aux Pays-Bas. L’ensemble des contenus, visuels et éléments graphiques de ce site sont protégés par le droit d’auteur.',
      }
    }
  };

  /**
   * Helper to replace Dutch keywords with target language equivalents in policy bodies
   */
  function translatePolicyDOM() {
    const lang = (document.documentElement.lang || window.Shopify?.locale || 'nl').toLowerCase().slice(0, 2);
    if (lang === 'nl') return; // Dutch is source

    const path = window.location.pathname.toLowerCase();
    const isPolicyPage = path.includes('/policies/') || path.includes('/pages/contact') || path.includes('/pages/company-information') || document.querySelector('.shopify-policy__container, .shopify-policy__body');

    if (!isPolicyPage) return;

    const titleEl = document.querySelector('.shopify-policy__title h1, .shopify-policy__container h1, .main-page-title, h1.policy-title');
    const bodyEl = document.querySelector('.shopify-policy__body, .shopify-policy__container, .page-content, .main-page-content');

    if (!bodyEl) return;

    // Detect Policy Type
    let pType = null;
    if (path.includes('contact') || (bodyEl.innerText && bodyEl.innerText.includes('Bedrijfsgegevens'))) pType = 'contact';
    else if (path.includes('shipping') || (bodyEl.innerText && bodyEl.innerText.includes('Verzendbeleid'))) pType = 'shipping';
    else if (path.includes('refund') || (bodyEl.innerText && bodyEl.innerText.includes('Terugbetalingsbeleid'))) pType = 'refund';
    else if (path.includes('privacy') || (bodyEl.innerText && bodyEl.innerText.includes('Privacybeleid'))) pType = 'privacy';
    else if (path.includes('terms') || (bodyEl.innerText && bodyEl.innerText.includes('Algemene voorwaarden'))) pType = 'terms';
    else if (path.includes('legal') || (bodyEl.innerText && bodyEl.innerText.includes('Juridische'))) pType = 'legal';

    // If specific dictionary exists and body is in Dutch
    if (pType && POLICY_TRANSLATIONS[pType] && POLICY_TRANSLATIONS[pType][lang]) {
      const t = POLICY_TRANSLATIONS[pType][lang];
      if (titleEl && (titleEl.innerText.includes('Bedrijfsgegevens') || titleEl.innerText.includes('beleid') || titleEl.innerText.includes('voorwaarden') || titleEl.innerText.includes('Contact Information') || titleEl.innerText.includes('Policy'))) {
        titleEl.textContent = t.title;
      }
    }

    // Translate common Dutch legal labels inside the body if they appear
    const dictionary = {
      de: [
        ['Bedrijfsgegevens & contact', 'Unternehmensdaten & Kontakt'],
        ['Bedrijfsgegevens', 'Unternehmensdaten'],
        ['Bedrijfsnaam', 'Firmenname'],
        ['Handelsnaam', 'Handelsname'],
        ['Vestigingsadres', 'Geschäftsadresse'],
        ['KvK-nummer', 'Handelsregisternummer (KvK)'],
        ['BTW-nummer', 'USt-IdNr. (BTW)'],
        ['E-mailadres', 'E-Mail-Adresse'],
        ['Telefoonnummer', 'Telefonnummer'],
        ['Heb je een vraag over je bestelling', 'Haben Sie eine Frage zu Ihrer Bestellung'],
        ['Neem gerust contact met ons op', 'Zögern Sie nicht, uns zu kontaktieren'],
        ['We helpen je graag verder', 'Wir helfen Ihnen gerne weiter'],
        ['Nederland', 'Niederlande'],
        ['Verzendbeleid', 'Versandrichtlinie'],
        ['Terugbetalingsbeleid', 'Rückgaberichtlinie'],
        ['Privacybeleid', 'Datenschutzerklärung'],
        ['Algemene voorwaarden', 'Allgemeine Geschäftsbedingungen'],
      ],
      fr: [
        ['Bedrijfsgegevens & contact', 'Informations sur l’entreprise & contact'],
        ['Bedrijfsgegevens', 'Informations sur l’entreprise'],
        ['Bedrijfsnaam', 'Raison sociale'],
        ['Handelsnaam', 'Nom commercial'],
        ['Vestigingsadres', 'Adresse du siège'],
        ['KvK-nummer', 'Numéro d’immatriculation (KvK)'],
        ['BTW-nummer', 'Numéro de TVA'],
        ['E-mailadres', 'Adresse e-mail'],
        ['Telefoonnummer', 'Numéro de téléphone'],
        ['Heb je een vraag over je bestelling', 'Vous avez une question concernant votre commande'],
        ['Neem gerust contact met ons op', 'N’hésitez pas à nous contacter'],
        ['We helpen je graag verder', 'Nous sommes là pour vous aider'],
        ['Nederland', 'Pays-Bas'],
        ['Verzendbeleid', 'Politique d’expédition'],
        ['Terugbetalingsbeleid', 'Politique de remboursement'],
        ['Privacybeleid', 'Politique de confidentialité'],
        ['Algemene voorwaarden', 'Conditions générales de vente'],
      ],
      en: [
        ['Bedrijfsgegevens & contact', 'Company Details & Contact'],
        ['Bedrijfsgegevens', 'Company Details'],
        ['Bedrijfsnaam', 'Company Name'],
        ['Handelsnaam', 'Trade Name'],
        ['Vestigingsadres', 'Business Address'],
        ['KvK-nummer', 'Chamber of Commerce (KvK) Number'],
        ['BTW-nummer', 'VAT Number'],
        ['E-mailadres', 'Email Address'],
        ['Telefoonnummer', 'Phone Number'],
        ['Heb je een vraag over je bestelling', 'Do you have a question about your order'],
        ['Neem gerust contact met ons op', 'Feel free to contact us'],
        ['We helpen je graag verder', 'We are happy to help you'],
        ['Nederland', 'Netherlands'],
        ['Verzendbeleid', 'Shipping Policy'],
        ['Terugbetalingsbeleid', 'Refund Policy'],
        ['Privacybeleid', 'Privacy Policy'],
        ['Algemene voorwaarden', 'Terms of Service'],
      ]
    };

    if (dictionary[lang]) {
      const walker = document.createTreeWalker(bodyEl, NodeFilter.SHOW_TEXT, null, false);
      let node;
      while ((node = walker.nextNode())) {
        let text = node.nodeValue;
        for (const [dutch, trans] of dictionary[lang]) {
          if (text.includes(dutch)) {
            text = text.replaceAll(dutch, trans);
          }
        }
        node.nodeValue = text;
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', translatePolicyDOM);
  } else {
    translatePolicyDOM();
  }
})();
